import type { RGB } from '../types';

export const colorMap: Record<string, [number, number, number]> = {
  "Black": [0, 0, 0],
  "Night": [12, 12, 12],
  "Gunmetal": [42, 52, 57],
  "Midnight": [25, 25, 112],
  "Charcoal": [54, 69, 79],
  "Dark Gray": [169, 169, 169],
  "Light Gray": [211, 211, 211],
  "Silver": [192, 192, 192],
  "Gray": [128, 128, 128],
  "White": [255, 255, 255],
  "Red": [255, 0, 0],
  "Maroon": [128, 0, 0],
  "Crimson": [220, 20, 60],
  "Tomato": [255, 99, 71],
  "Coral": [255, 127, 80],
  "Salmon": [250, 128, 114],
  "Orange": [255, 165, 0],
  "Gold": [255, 215, 0],
  "Yellow": [255, 255, 0],
  "Olive": [128, 128, 0],
  "Lime": [0, 255, 0],
  "Green": [0, 128, 0],
  "Emerald": [80, 200, 120],
  "Teal": [0, 128, 128],
  "Cyan": [0, 255, 255],
  "Sky Blue": [135, 206, 235],
  "Royal Blue": [65, 105, 225],
  "Blue": [0, 0, 255],
  "Navy": [0, 0, 128],
  "Indigo": [75, 0, 130],
  "Purple": [128, 0, 128],
  "Violet": [238, 130, 238],
  "Magenta": [255, 0, 255],
  "Pink": [255, 192, 203],
  "Hot Pink": [255, 105, 180],
  "Rose": [255, 0, 127],
  "Brown": [165, 42, 42],
  "Chocolate": [210, 105, 30],
  "Tan": [210, 180, 140],
  "Beige": [245, 245, 220],
  "Slate": [112, 128, 144],
  "Lavender": [230, 230, 250],
  "Mint": [189, 252, 201],
  "Peach": [255, 218, 185],
  "Cream": [255, 253, 208],
  "Burgundy": [128, 0, 32],
  "Ochre": [204, 119, 34],
  "Amber": [255, 191, 0],
  "Cobalt": [0, 71, 171],
  "Cerulean": [0, 123, 167],
  "Mauve": [224, 176, 255],
  "Lilac": [200, 162, 200],
  "Periwinkle": [204, 204, 255],
  "Seafoam": [147, 225, 216],
  "Forest Green": [34, 139, 34],
  "Kelly Green": [76, 187, 23],
  "Ruby": [224, 17, 95],
  "Sapphire": [15, 82, 186],
  "Turquoise": [48, 213, 200],
  "Aquamarine": [127, 255, 212],
  "CSS Turquoise": [64, 224, 208],
  "Sage Green": [156, 175, 136],
  "Ivory": [255, 255, 240],
  "Rebecca Purple": [102, 51, 153]
};

export const getNearestColorName = (r: number, g: number, b: number): string => {
  let minDistance = Infinity;
  let nearestName = "Custom Color";

  for (const [name, rgb] of Object.entries(colorMap)) {
    // Euclidean distance
    const distance = Math.sqrt(
      Math.pow(r - rgb[0], 2) +
      Math.pow(g - rgb[1], 2) +
      Math.pow(b - rgb[2], 2)
    );

    if (distance < minDistance) {
      minDistance = distance;
      nearestName = name;
    }
  }
  
  return nearestName;
};

export const rgbToHex = (r: number, g: number, b: number) => {
  return "#" + [r, g, b].map(x => x.toString(16).padStart(2, '0').toUpperCase()).join('');
};

export const hexToRgb = (hex: string): RGB | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// --- Color Model Conversions ---

export const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

export const hslToRgb = (h: number, s: number, l: number) => {
  h /= 360; s /= 100; l /= 100;
  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
};

export const rgbToHsv = (r: number, g: number, b: number, round = true) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s, v = max;
  const d = max - min;
  s = max === 0 ? 0 : d / max;
  if (max === min) {
    h = 0;
  } else {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return round ? { h: Math.round(h * 360), s: Math.round(s * 100), v: Math.round(v * 100) } : { h: h * 360, s: s * 100, v: v * 100 };
};

export const hsvToRgb = (h: number, s: number, v: number) => {
  h /= 360; s /= 100; v /= 100;
  let r = 0, g = 0, b = 0;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
};

export const rgbToCmyk = (r: number, g: number, b: number) => {
  let c = 0, m = 0, y = 0, k = 0;
  r /= 255; g /= 255; b /= 255;
  k = 1 - Math.max(r, g, b);
  if (k === 1) {
    return { c: 0, m: 0, y: 0, k: 100 };
  }
  c = (1 - r - k) / (1 - k);
  m = (1 - g - k) / (1 - k);
  y = (1 - b - k) / (1 - k);
  return {
    c: Math.round(c * 100),
    m: Math.round(m * 100),
    y: Math.round(y * 100),
    k: Math.round(k * 100)
  };
};

export const cmykToRgb = (c: number, m: number, y: number, k: number) => {
  c /= 100; m /= 100; y /= 100; k /= 100;
  const r = 255 * (1 - c) * (1 - k);
  const g = 255 * (1 - m) * (1 - k);
  const b = 255 * (1 - y) * (1 - k);
  return {
    r: Math.round(r),
    g: Math.round(g),
    b: Math.round(b)
  };
};

// --- OKLCH Color Space ---

// sRGB → linear RGB (gamma decode)
const srgbToLinear = (c: number): number => {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

// linear RGB → sRGB (gamma encode)
const linearToSrgb = (c: number): number => {
  const val = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.round(Math.max(0, Math.min(255, val * 255)));
};

export interface OKLCH {
  l: number;  // Lightness 0-100
  c: number;  // Chroma 0-0.4 (displayed as 0-40 for readability)
  h: number;  // Hue 0-360
}

export const rgbToOklab = (r: number, g: number, b: number) => {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  // Linear RGB → LMS (using Oklab M1 matrix)
  const l_ = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m_ = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s_ = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  // Cube root
  const l = Math.cbrt(l_);
  const m = Math.cbrt(m_);
  const s = Math.cbrt(s_);

  // LMS → Oklab (M2 matrix)
  return {
    L: 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  };
};

export const oklabToRgb = (L: number, a: number, b: number): RGB => {
  // Oklab → LMS (inverse M2)
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  // Cube
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  // LMS → linear RGB (inverse M1)
  const lr =  4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  return {
    r: linearToSrgb(lr),
    g: linearToSrgb(lg),
    b: linearToSrgb(lb),
  };
};

export const rgbToOklch = (r: number, g: number, b: number): OKLCH => {
  const lab = rgbToOklab(r, g, b);
  const rawC = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  let h = Math.atan2(lab.b, lab.a) * (180 / Math.PI);
  if (h < 0) h += 360;

  return {
    l: Math.round(lab.L * 1000) / 10,     // 0-100 (1 decimal)
    c: Math.round(rawC * 1000) / 10,       // 0-40 scale (raw 0-0.4 × 100)
    h: Math.round(h * 10) / 10,            // 0-360 (1 decimal)
  };
};

export const oklchToRgb = (l: number, c: number, h: number): RGB => {
  // l: 0-100, c: 0-40 (scale back to 0-0.4), h: 0-360
  const hRad = h * (Math.PI / 180);
  const rawC = c / 100;
  const a = rawC * Math.cos(hRad);
  const b = rawC * Math.sin(hRad);
  return oklabToRgb(l / 100, a, b);
};

// OKLCH interpolation in Oklab space for perceptually uniform gradients
export const interpolateOklab = (c1: RGB, c2: RGB, t: number): RGB => {
  const lab1 = rgbToOklab(c1.r, c1.g, c1.b);
  const lab2 = rgbToOklab(c2.r, c2.g, c2.b);

  const L = lab1.L + (lab2.L - lab1.L) * t;
  const a = lab1.a + (lab2.a - lab1.a) * t;
  const b = lab1.b + (lab2.b - lab1.b) * t;

  return oklabToRgb(L, a, b);
};
